import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import publicOutreachService, { PublicOutreachCampaign } from '../services/publicOutreachService';
import gtmService, { Icp } from '../services/gtmService';
import { PageHeader, useConfirm } from '../components/ui';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { rollenAufzaehlung, rollenMitRecht } from '../verwaltung/rollen';

const STATUS_BADGES: Record<string, { label: string; className: string }> = {
  draft: { label: 'Entwurf', className: 'badge-secondary' },
  researching: { label: 'Recherchiert…', className: 'badge-info' },
  collecting: { label: 'Sammelt Adressen', className: 'badge-info' },
  ready: { label: 'Versandbereit', className: 'badge-warning' },
  active: { label: 'Versendet', className: 'badge-success' },
  completed: { label: 'Abgeschlossen', className: 'badge-success' },
  failed: { label: 'Fehlgeschlagen', className: 'badge-error' },
};

const PublicOutreachPage: React.FC = () => {
  const { confirm } = useConfirm();
  // Welle 2: der Router public-outreach liegt im Bereich Versand; jedes
  // Anlegen, Aendern und Loeschen verlangt versand.ausfuehren.
  const { darf } = useBerechtigung();
  const darfAusfuehren = darf('versand.ausfuehren');
  const [campaigns, setCampaigns] = useState<PublicOutreachCampaign[]>([]);
  const [icps, setIcps] = useState<Icp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    targetRegion: 'Deutschland',
    targetIndustries: [] as string[],
    nearLocations: '',
    specificLocations: [] as string[],
    additionalCriteria: '',
    goal: '',
    legalBasisNote: 'Sachbezogene B2B-Anfrage an die öffentlich publizierte Kontaktadresse (Art. 6 (1)(f) DSGVO i.V.m. §7 UWG).',
    icpId: '',
  });

  useEffect(() => {
    loadCampaigns();
  }, []);

  // ICPs braucht nur das Anlegeformular.
  useEffect(() => {
    if (!darfAusfuehren) return;
    gtmService.getIcps().then(setIcps).catch(() => undefined);
  }, [darfAusfuehren]);

  const loadCampaigns = async () => {
    try {
      setCampaigns(await publicOutreachService.getCampaigns());
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kampagnen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const created = await publicOutreachService.createCampaign({
        name: formData.name,
        description: formData.description || undefined,
        targetRegion: formData.targetRegion,
        targetIndustries: formData.targetIndustries.length ? formData.targetIndustries : undefined,
        targetCriteria: {
          nearLocations: formData.nearLocations || undefined,
          specificLocations: formData.specificLocations.length ? formData.specificLocations : undefined,
          additionalCriteria: formData.additionalCriteria || undefined,
        },
        goal: formData.goal || undefined,
        legalBasisNote: formData.legalBasisNote || undefined,
        icpId: formData.icpId || undefined,
      });
      setShowCreateForm(false);
      navigate(`/public-outreach/${created.id}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kampagne konnte nicht erstellt werden');
    }
  };

  const handleDelete = async (campaign: PublicOutreachCampaign) => {
    const ok = await confirm({
      title: `Kampagne „${campaign.name}“ löschen?`,
      message: 'Die Kampagne wird mit allen gesammelten Firmenadressen dauerhaft gelöscht. Rückgängig nur durch Neuanlage.',
      confirmLabel: 'Löschen',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await publicOutreachService.deleteCampaign(campaign.id);
      loadCampaigns();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Löschen fehlgeschlagen');
    }
  };

  const badge = (status: string) => {
    const info = STATUS_BADGES[status] || { label: status, className: 'badge-secondary' };
    return <span className={`badge ${info.className}`}>{info.label}</span>;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <div className="loading-text">Lade Firmen-Mailings…</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <PageHeader
        title="Firmen-Mailing"
        subtitle="Für Erstkontakt über die öffentliche Firmenadresse, wenn kein Ansprechpartner bekannt ist."
        actions={darfAusfuehren ? (
          <button className="btn btn-success" onClick={() => setShowCreateForm(!showCreateForm)}>
            {showCreateForm ? 'Abbrechen' : 'Neue Kampagne'}
          </button>
        ) : undefined}
      />

      {!darfAusfuehren && (
        <p className="text-sm text-muted" data-testid="rechte-hinweis">
          Anlegen, Recherchieren und Versenden sind {rollenAufzaehlung(rollenMitRecht('versand.ausfuehren'))} vorbehalten.
        </p>
      )}

      <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
        <strong>Legal an Firmen schreiben.</strong> Diese Funktion recherchiert Firmen, sammelt
        ausschließlich <em>öffentliche Sammeladressen</em> (info@/kontakt@ aus dem Impressum) inkl.
        Quellennachweis und versendet über Lemlist mit Pflicht-Abmeldelink. Personenbezogene
        Einzeladressen werden bewusst nicht verwendet.
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {showCreateForm && darfAusfuehren && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <form onSubmit={handleCreate}>
            <h3 className="card-title">Neue Firmen-Mailing-Kampagne</h3>

            <div className="form-group">
              <label>Kampagnenname *</label>
              <input
                type="text"
                className="form-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="z.B. IT-Dienstleister Rhein-Main 2026"
              />
            </div>

            <div className="form-group">
              <label>Beschreibung</label>
              <textarea
                className="form-textarea"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>

            <div className="form-group">
              <label>ICP-Grounding (optional)</label>
              <select
                className="form-input"
                value={formData.icpId}
                onChange={(e) => setFormData({ ...formData, icpId: e.target.value })}
              >
                <option value="">– kein ICP –</option>
                {icps.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Zielregion *</label>
              <input
                type="text"
                className="form-input"
                value={formData.targetRegion}
                onChange={(e) => setFormData({ ...formData, targetRegion: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Zielbranchen (durch Komma getrennt)</label>
              <input
                type="text"
                className="form-input"
                value={formData.targetIndustries.join(', ')}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    targetIndustries: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  })
                }
                placeholder="z.B. Maschinenbau, Logistik"
              />
            </div>

            <div className="form-group">
              <label>Spezifische Standorte (durch Komma getrennt)</label>
              <input
                type="text"
                className="form-input"
                value={formData.specificLocations.join(', ')}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    specificLocations: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  })
                }
                placeholder="z.B. Frankfurt, Mainz, Wiesbaden"
              />
            </div>

            <div className="form-group">
              <label>Zusätzliche Kriterien</label>
              <textarea
                className="form-textarea"
                value={formData.additionalCriteria}
                onChange={(e) => setFormData({ ...formData, additionalCriteria: e.target.value })}
                rows={2}
              />
            </div>

            <div className="form-group">
              <label>Anliegen / CTA der Nachricht</label>
              <input
                type="text"
                className="form-input"
                value={formData.goal}
                onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
                placeholder="z.B. kurzes Gespräch zu Security-Awareness anbieten"
              />
            </div>

            <div className="form-group">
              <label>Dokumentierte Rechtsgrundlage</label>
              <textarea
                className="form-textarea"
                value={formData.legalBasisNote}
                onChange={(e) => setFormData({ ...formData, legalBasisNote: e.target.value })}
                rows={2}
              />
              <p className="text-xs text-muted" style={{ marginTop: '0.25rem' }}>
                Wird je versendeter Mail als Nachweis mitgeführt (DSGVO Art. 30).
              </p>
            </div>

            <div className="card-footer">
              <button type="submit" className="btn btn-primary">
                Kampagne erstellen
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowCreateForm(false)}>
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      {campaigns.length === 0 ? (
        <div className="empty-state">
          <p className="empty-state-description">
            {darfAusfuehren ? 'Noch keine Firmen-Mailings. Erstellen Sie Ihre erste Kampagne!' : 'Noch keine Firmen-Mailings.'}
          </p>
        </div>
      ) : (
        <div className="grid-cards">
          {campaigns.map((c) => (
            <div key={c.id} className="card">
              <div className="card-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <h3 className="card-title">{c.name}</h3>
                  {badge(c.status)}
                </div>
                {c.description && <p className="card-description">{c.description}</p>}
                <div className="text-xs text-muted" style={{ marginTop: '0.5rem' }}>
                  <div>Region: {c.targetRegion}</div>
                  {c._count && <div>Firmen: {c._count.targets}</div>}
                  <div>Erstellt: {new Date(c.createdAt).toLocaleDateString()}</div>
                </div>
              </div>
              <div className="card-footer">
                <button className="btn btn-primary btn-sm" onClick={() => navigate(`/public-outreach/${c.id}`)}>
                  Öffnen
                </button>
                {darfAusfuehren && (
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c)}>
                    Löschen
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PublicOutreachPage;
